import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth/AuthProvider'
import { deleteAccountRequest } from '../../auth/deleteAccount'
import { fetchMe, apiFetch, type SessionUser } from '../../lib/pfhApi'
import { PageShell, SectionList, Row, FieldRow, Note } from './SectionList'
import { requestStepup } from '../../auth/stepupStore'
import { PasswordField } from '../../components/PasswordField'

import { normalizeSessions, type DeviceSession } from '../../auth/sessions'
import { apiErrorMessage } from '../../lib/apiError'

export function SecurityPage() {
  const { t } = useTranslation()
  const { logout } = useAuth()
  const [deleting, setDeleting] = useState(false)
  const [delPw, setDelPw] = useState('')
  const [delBusy, setDelBusy] = useState(false)
  const [delErr, setDelErr] = useState<string | null>(null)
  const [user, setUser] = useState<SessionUser | null>(null)
  const [sessions, setSessions] = useState<DeviceSession[]>([])
  const [changing, setChanging] = useState(false)
  const [curPw, setCurPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [msg, setMsg] = useState<string | null>(null)

  async function loadSessions() {
    const r = await apiFetch('/auth/sessions')
    if (r.ok) setSessions(normalizeSessions(await r.json().catch(() => null)))
  }

  useEffect(() => {
    void fetchMe().then(setUser)
    void loadSessions()
  }, [])

  async function changePassword() {
    setMsg(null)
    const r = await apiFetch('/auth/me/password', {
      method: 'PUT',
      body: JSON.stringify({ current_password: curPw, new_password: newPw }),
    })
    if (r.ok) {
      setMsg('Password updated.')
      setChanging(false)
      setCurPw(''); setNewPw('')
    } else {
      const body = await r.json().catch(() => ({}))
      setMsg(apiErrorMessage(body, 'Something went wrong. Please try again.'))
    }
  }

  async function turnOnTwoStep() {
    // The verification sheet enrols accounts with no method yet (email code), then returns here.
    if (await requestStepup('pazuru')) setUser(await fetchMe())
  }

  async function revoke(id: number) {
    await apiFetch(`/auth/sessions/${id}`, { method: 'DELETE' })
    await loadSessions()
  }

  async function deleteAccount() {
    setDelErr(null)
    setDelBusy(true)
    const res = await deleteAccountRequest(apiFetch, delPw)
    setDelBusy(false)
    if (res.ok) {
      setDelPw('')
      await logout() // clears local tokens + session; route guards send the user to /login
    } else {
      setDelErr(res.message)
    }
  }

  const inputStyle = {
    background: 'var(--color-bg-primary)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-bg-border)',
    borderRadius: 8,
    padding: '6px 44px 6px 10px',
    fontSize: 15,
    width: 140,
  } as const

  return (
    <PageShell title="Security">
      <SectionList title="Sign-in protection">
        <Row label="Two-step verification" value={user?.mfa_enabled ? 'On' : 'Off'} />
        {user && !user.mfa_enabled && <Row label="Turn on" onClick={turnOnTwoStep} />}
      </SectionList>
      <Note>
        Two-step verification asks for a code from your email or an authenticator app when you open
        sensitive data, so a stolen password alone is not enough.
      </Note>

      <SectionList title="Password">
        {changing ? (
          <>
            <Row label="Current" trailing={<PasswordField value={curPw} onChange={(e) => setCurPw(e.target.value)} style={inputStyle} />} />
            <Row label="New" trailing={<PasswordField value={newPw} onChange={(e) => setNewPw(e.target.value)} style={inputStyle} />} />
            <Row label="Confirm" onClick={changePassword} />
            <Row label="Cancel" onClick={() => setChanging(false)} />
          </>
        ) : (
          <Row label="Change password" onClick={() => setChanging(true)} />
        )}
        {msg && <Row label={msg} />}
      </SectionList>

      <SectionList title="Active devices">
        {sessions.length === 0 && <Row label="No active sessions" />}
        {sessions.map((s) => (
          <Row
            key={s.id}
            label={s.device_label + (s.current ? ' (this device)' : '')}
            value={s.current ? undefined : 'Revoke'}
            onClick={s.current ? undefined : () => revoke(s.id)}
            destructive={!s.current}
          />
        ))}
      </SectionList>

      <SectionList title={t('mobile.deleteAccount.title')}>
        {deleting ? (
          <>
            <FieldRow label={t('mobile.deleteAccount.password')}>
              <PasswordField
                value={delPw}
                onChange={(e) => setDelPw(e.target.value)}
                autoComplete="current-password"
                disabled={delBusy}
                style={{ ...inputStyle, width: '100%' }}
              />
            </FieldRow>
            <Row
              label={delBusy ? t('mobile.deleteAccount.deleting') : t('mobile.deleteAccount.confirm')}
              onClick={deleteAccount}
              disabled={delBusy || delPw.length === 0}
              destructive
            />
            <Row label={t('mobile.deleteAccount.cancel')} onClick={() => { setDeleting(false); setDelPw(''); setDelErr(null) }} disabled={delBusy} />
          </>
        ) : (
          <Row label={t('mobile.deleteAccount.open')} onClick={() => setDeleting(true)} destructive />
        )}
        {delErr && <Row label={delErr} />}
      </SectionList>
      <Note>{t('mobile.deleteAccount.warning')}</Note>
    </PageShell>
  )
}
